import { useContext } from 'react'
import type { IWebTelemetryService } from '@ztechapps/common.contract'
import { TelemetryContext } from '../context/TelemetryContext'

/**
 * Hook to access the WebTelemetryService
 *
 * @returns The telemetry service instance, or null if not available
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const telemetry = useTelemetry()
 *
 *   const handleClick = () => {
 *     telemetry?.trackEvent('button_click', { button: 'submit' })
 *   }
 *
 *   return <button onClick={handleClick}>Submit</button>
 * }
 * ```
 */
export function useTelemetry(): IWebTelemetryService | null {
  return useContext(TelemetryContext)
}

/**
 * Hook to access the WebTelemetryService with guaranteed availability
 *
 * @throws Error if used outside TelemetryProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const telemetry = useTelemetryRequired()
 *   telemetry.trackEvent('view', { component: 'MyComponent' })
 * }
 * ```
 */
export function useTelemetryRequired(): IWebTelemetryService {
  const telemetry = useContext(TelemetryContext)
  if (!telemetry) {
    throw new Error('useTelemetryRequired must be used within a TelemetryProvider')
  }
  return telemetry
}
